/**
 * Data-grid toolkit for the admin lists: state in the URL (page, size, sort, filters — links
 * can be shared and the back button works), sortable headers, a pagination bar, a filter
 * toolbar, clickable rows and a loader that keeps the previous page on screen while the next
 * one loads.
 */
import type { paths } from '@mrigl/api-client';
import { SelectField } from '@mrigl/ui';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from './api.js';
import { useI18n } from './i18n.js';

export const PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

export interface Paged<T> {
  readonly items: T[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
}

/** `field` ascending or `-field` descending. */
export type SortValue = string;

/** Query parameters of a GET list endpoint (filters and sort values typed from the API). */
export type ListQuery<P extends keyof paths> = paths[P] extends {
  get: { parameters: { query?: infer Q } };
}
  ? NonNullable<Q>
  : never;

export interface GridState<F extends string> {
  readonly page: number;
  readonly pageSize: number;
  readonly sort: SortValue;
  readonly filters: Readonly<Record<F, string>>;
  /** Filters that differ from their defaults (for the reset button). */
  readonly activeFilters: number;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (size: number) => void;
  readonly setSort: (sort: SortValue) => void;
  readonly setFilter: (name: F, value: string) => void;
  readonly reset: () => void;
}

/**
 * Grid state kept in the query string. Changing a filter, the sort or the page size goes back
 * to page 1. Defaults are left out of the URL so the plain list address stays clean.
 */
export function useGridState<F extends string>(defaults: {
  readonly sort: SortValue;
  /** Sortable fields; an unknown `sort` in the URL falls back to the default. */
  readonly sortable: readonly string[];
  readonly filters: Readonly<Record<F, string>>;
}): GridState<F> {
  const [params, setParams] = useSearchParams();
  const names = Object.keys(defaults.filters) as F[];

  const rawPage = Number(params.get('page'));
  const page = Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1;
  const rawSize = Number(params.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(rawSize)
    ? rawSize
    : DEFAULT_PAGE_SIZE;
  const wantedSort = params.get('sort') ?? defaults.sort;
  const sort = defaults.sortable.includes(wantedSort.replace(/^-/, ''))
    ? wantedSort
    : defaults.sort;
  const filterKey = names.map((name) => `${name}=${params.get(name) ?? ''}`).join('&');
  const filters = useMemo(
    () =>
      Object.fromEntries(
        names.map((name) => [name, params.get(name) ?? defaults.filters[name]]),
      ) as Record<F, string>,
    // filterKey captures every value read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );

  const update = useCallback(
    (changes: Record<string, string | null>, resetPage: boolean) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    page,
    pageSize,
    sort,
    filters,
    activeFilters: names.filter((name) => filters[name] !== defaults.filters[name]).length,
    setPage: (value) => {
      update({ page: value <= 1 ? null : String(value) }, false);
    },
    setPageSize: (value) => {
      update({ size: value === DEFAULT_PAGE_SIZE ? null : String(value) }, true);
    },
    setSort: (value) => {
      update({ sort: value === defaults.sort ? null : value }, true);
    },
    setFilter: (name, value) => {
      update({ [name]: value === defaults.filters[name] ? null : value }, true);
    },
    reset: () => {
      update(Object.fromEntries(names.map((name) => [name, null])), true);
    },
  };
}

/**
 * Loads a page whenever `key` changes. The previous page stays visible (dimmed) while the next
 * one loads; responses to superseded requests are ignored. A page past the end (after
 * deletions or a narrower filter) moves back to the last page.
 */
export function usePagedData<T>(
  key: string,
  load: () => Promise<Paged<T>>,
  onBeyondEnd?: (lastPage: number) => void,
) {
  const { t } = useI18n();
  const [data, setData] = useState<Paged<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const beyondRef = useRef(onBeyondEnd);
  beyondRef.current = onBeyondEnd;
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const request = ++latest.current;
    setLoading(true);
    void (async () => {
      try {
        const page = await loadRef.current();
        if (request !== latest.current) return;
        const lastPage = Math.max(1, Math.ceil(page.total / page.pageSize));
        if (page.items.length === 0 && page.total > 0 && page.page > lastPage) {
          beyondRef.current?.(lastPage);
          return;
        }
        setData(page);
        setError(null);
      } catch (failure) {
        if (request !== latest.current) return;
        setError(errorMessage(t, failure));
      } finally {
        if (request === latest.current) setLoading(false);
      }
    })();
  }, [key, version, t]);

  const reload = useCallback(() => {
    setVersion((value) => value + 1);
  }, []);
  return { data, loading, error, reload };
}

/** Text filter that updates the grid after a short pause in typing. */
export function SearchFilter({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const id = useId();
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // The value this field last sent: its echo through the URL must not overwrite what the
  // user typed since (that would drop keystrokes after a pause).
  const sent = useRef(value);
  // Follows outside changes only (reset button, back navigation).
  useEffect(() => {
    if (value === sent.current) return;
    sent.current = value;
    setText(value);
  }, [value]);
  useEffect(() => {
    if (text.trim() === value) return;
    const timer = setTimeout(() => {
      sent.current = text.trim();
      onChangeRef.current(text.trim());
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [text, value]);
  return (
    <div className="mrigl-field admin-search">
      <label className="mrigl-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="admin-search__box">
        <Search className="admin-search__icon" size={16} aria-hidden="true" />
        <input
          id={id}
          className="mrigl-input"
          type="search"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
      </div>
    </div>
  );
}

/**
 * Filters row: the search stays visible; on narrow screens the other filters fold behind a
 * "Filters" toggle showing how many are active. Reset appears when something is filtered.
 */
export function GridToolbar({
  search,
  children,
  activeFilters,
  onReset,
}: {
  readonly search?: ReactNode;
  readonly children?: ReactNode;
  readonly activeFilters: number;
  readonly onReset: () => void;
}) {
  const { t } = useI18n();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  return (
    <div
      className="admin-toolbar"
      role="group"
      aria-label={t('admin.grid.filters')}
      data-open={open ? 'true' : undefined}
    >
      <div className="admin-toolbar__filters">
        {search}
        {children === undefined ? null : (
          <>
            <button
              type="button"
              className="mrigl-button mrigl-button--secondary admin-toolbar__toggle"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => {
                setOpen((value) => !value);
              }}
            >
              <SlidersHorizontal size={16} aria-hidden="true" />
              {t('admin.grid.filters')}
              {activeFilters > 0 ? (
                <span className="admin-toolbar__badge">{activeFilters}</span>
              ) : null}
            </button>
            <div id={panelId} className="admin-toolbar__more">
              {children}
            </div>
          </>
        )}
      </div>
      {activeFilters > 0 ? (
        <button type="button" className="mrigl-button mrigl-button--secondary" onClick={onReset}>
          <RotateCcw size={16} aria-hidden="true" />
          {t('admin.grid.reset')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Column header that sorts on click: first click uses `firstDescending` (dates and amounts read
 * best newest/largest first), the next click flips the direction.
 */
export function SortHeader({
  field,
  label,
  sort,
  onSort,
  firstDescending = false,
  numeric = false,
}: {
  readonly field: string;
  readonly label: string;
  readonly sort: SortValue;
  readonly onSort: (sort: SortValue) => void;
  readonly firstDescending?: boolean;
  readonly numeric?: boolean;
}) {
  const active = sort === field || sort === `-${field}`;
  const descending = sort === `-${field}`;
  const Icon = !active ? ChevronsUpDown : descending ? ArrowDown : ArrowUp;
  return (
    <th
      scope="col"
      aria-sort={!active ? 'none' : descending ? 'descending' : 'ascending'}
      className={numeric ? 'admin-num' : undefined}
    >
      <button
        type="button"
        className={`admin-sort${active ? ' admin-sort--active' : ''}`}
        onClick={() => {
          if (!active) onSort(firstDescending ? `-${field}` : field);
          else onSort(descending ? field : `-${field}`);
        }}
      >
        {label}
        <Icon size={14} aria-hidden="true" />
      </button>
    </th>
  );
}

/** Page numbers to show: first, last, the current one and its neighbours, gaps as null. */
export function pageWindow(page: number, pages: number): (number | null)[] {
  const wanted = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
  const sorted = [...wanted].sort((a, b) => a - b);
  const result: (number | null)[] = [];
  for (const value of sorted) {
    const previous = result.at(-1);
    if (typeof previous === 'number' && value - previous > 1) result.push(null);
    result.push(value);
  }
  return result;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
}: {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
  readonly onPageSize: (size: number) => void;
}) {
  const { t } = useI18n();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="admin-pager">
      <p className="admin-pager__range" aria-live="polite">
        {t('admin.grid.range', { from, to, total })}
      </p>
      <SelectField
        label={t('admin.grid.pageSize')}
        value={String(pageSize)}
        onChange={(event) => {
          onPageSize(Number(event.target.value));
        }}
        options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
      />
      {pages <= 1 ? null : (
        <nav className="admin-pager__nav" aria-label={t('admin.grid.pagination')}>
          <button
            type="button"
            className="admin-pager__button"
            aria-label={t('admin.grid.previous')}
            disabled={page <= 1}
            onClick={() => {
              onPage(page - 1);
            }}
          >
            <ChevronLeft className="admin-pager__icon" size={16} aria-hidden="true" />
          </button>
          {pageWindow(page, pages).map((value, index) =>
            value === null ? (
              <span key={`gap-${String(index)}`} className="admin-pager__gap" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={value}
                type="button"
                className="admin-pager__button"
                aria-label={t('admin.grid.page', { page: value })}
                aria-current={value === page ? 'page' : undefined}
                onClick={() => {
                  onPage(value);
                }}
              >
                {value}
              </button>
            ),
          )}
          <button
            type="button"
            className="admin-pager__button"
            aria-label={t('admin.grid.next')}
            disabled={page >= pages}
            onClick={() => {
              onPage(page + 1);
            }}
          >
            <ChevronRight className="admin-pager__icon" size={16} aria-hidden="true" />
          </button>
        </nav>
      )}
    </div>
  );
}

/**
 * Makes a whole table row open `to` on click (the row keeps a real link in its first cell
 * for keyboards and screen readers). Clicks on controls, links and text selections are left
 * alone; Ctrl/⌘-click opens a new tab like a link would.
 */
export function useRowLink() {
  const navigate = useNavigate();
  return (to: string) => ({
    className: 'admin-row-link',
    onClick: (event: MouseEvent<HTMLTableRowElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('a, button, input, select, textarea, label') !== null) return;
      if ((globalThis.getSelection()?.toString() ?? '') !== '') return;
      if (event.ctrlKey || event.metaKey) {
        globalThis.open(to, '_blank', 'noopener');
        return;
      }
      void navigate(to);
    },
  });
}

/** The table area: dims while loading and shows the right empty message. */
export function GridBody({
  loading,
  empty,
  filtered,
  emptyText,
  children,
}: {
  readonly loading: boolean;
  readonly empty: boolean;
  readonly filtered: boolean;
  readonly emptyText: string;
  readonly children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="admin-grid" aria-busy={loading}>
      {empty ? (
        <p className="admin-grid__empty">{filtered ? t('admin.grid.noResults') : emptyText}</p>
      ) : (
        <div className="admin-table-wrap">{children}</div>
      )}
    </div>
  );
}
